import { useState } from "react";
import { InputFieldRow, InputFieldRowTableModel, InputFieldTypeEnum } from "../InputFields.types.ts";
import { useStateUrlParamsArray } from "../../../common/hooks/useStateUrlParams.ts";
import { DotLegalSelectOption } from "../../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types.ts";
import { BaseViewModel } from "../../../system/systemOverview/SystemOverview.types.ts";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "../../../common/stringOperations.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";

import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { CustomInputFieldTabProps } from "./CustomInputFieldTab.tsx";

export function useCustomInputFieldTab(props: CustomInputFieldTabProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();

    const [inputFieldToEdit, setInputFieldToEdit] = useState<string | undefined>(undefined);
    const [inputFieldForDeletion, setInputFieldForDeletion] = useState<InputFieldRow | undefined>(undefined);

    // URL filters
    const [searchedInputTypes, setSearchedInputTypes] = useStateUrlParamsArray("inputTypes", []);
    const [searchedEntityTemplates, setSearchedEntityTemplates] = useStateUrlParamsArray("contractTemplates", []);

    const url = `/settings/input-fields?templateEntityType=${props.templateEntityType}`;
    const { isLoading, data: inputFields, refetch, isIdle } = useDotLegalQuery<Array<InputFieldRow>>(url);

    const onSaved = async () => {
        await refetch();
        setInputFieldToEdit(undefined);
        setInputFieldForDeletion(undefined);
    };

    const canEditInputFields = permissions.canManageMasterData;

    const getMenuItems = (contractInputField: InputFieldRow) => {
        let menuItems = [];
        if (canEditInputFields) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setInputFieldToEdit(contractInputField.id);
                },
            });

            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setInputFieldForDeletion(contractInputField);
                },
            });
        }

        return menuItems;
    };

    let inputTypeFilterOptions: Array<DotLegalSelectOption> = [];
    let entityTemplateFilterOptions: Array<DotLegalSelectOption> = [];
    if (inputFields) {
        if (inputFields) {
            createInputTypeFilterOptions(inputFields.map((x) => x.type));
            createEntityTemplateOptions(inputFields.flatMap((x) => x.entityTemplates ?? []));
        }
    }

    const hasInputFields = isIdle || isLoading || (inputFields && inputFields.length > 0);
    return {
        isLoading,
        hasInputFields,
        inputFields: getTableData(),
        inputFieldToEdit,
        setInputFieldToEdit,
        canEditInputFields,
        getMenuItems,
        onSaved,
        inputFieldForDeletion,
        setInputFieldForDeletion,
        inputTypeFilterOptions,
        entityTemplateFilterOptions,
        searchedInputTypes,
        setSearchedInputTypes,
        searchedEntityTemplates,
        setSearchedEntityTemplates,
    };

    function createEntityTemplateOptions(templates: Array<BaseViewModel>) {
        templates.forEach((template) => {
            if (!entityTemplateFilterOptions.some((s) => s.id === template.id)) {
                entityTemplateFilterOptions.push({ name: template.name, id: template.id });
            }
        });

        if (templates.length > 0) {
            entityTemplateFilterOptions.push({ name: translateString("none"), id: "null" });
        }
    }

    function createInputTypeFilterOptions(contractInputFieldTypeEnums: Array<InputFieldTypeEnum>) {
        contractInputFieldTypeEnums.forEach((x) => {
            if (!inputTypeFilterOptions.some((s) => s.id === x.toString())) {
                inputTypeFilterOptions.push({ id: x.toString(), name: translateString(setFirstLetterToLowerCase(InputFieldTypeEnum[x].toString())) });
            }
        });
    }

    function getTableData(): Array<InputFieldRowTableModel> {
        const rows = getFilteredData();

        return rows.map((x) => {
            return {
                ...x,
                entityTemplatesString: x.entityTemplates.map((x) => x.name).join(", "),
                typeString: translateString(setFirstLetterToLowerCase(InputFieldTypeEnum[x.type].toString())),
            };
        });
    }

    function getFilteredData() {
        if (!inputFields) return [];

        let tempData = [...inputFields];

        if (searchedInputTypes.length > 0 && !isNullOrWhitespace(searchedInputTypes[0])) {
            tempData = tempData.filter((row) => searchedInputTypes.includes(row.type.toString()));
        }

        if (searchedEntityTemplates.length > 0 && !isNullOrWhitespace(searchedEntityTemplates[0])) {
            tempData = tempData.filter(
                (row) =>
                    row.entityTemplates.some((template) => searchedEntityTemplates.includes(template.id)) ||
                    (searchedEntityTemplates.includes("null") && row.entityTemplates.length === 0)
            );
        }

        return tempData;
    }
}
